import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { db } from "../data/base";
import { collection, doc, getDoc, updateDoc, setDoc, query, where, getDocs, runTransaction } from "firebase/firestore";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import ModelContainerFrontEnd from "../components/ModelViewer/ModelContainerFrontEnd";
import ProjectModelViewer from "../components/ModelViewer/ProjectModelViewer";
import ThreeJSViewer from "../components/ModelViewer/ThreeJSViewer/ThreeJSViewer";
import Password from "../components/ModelViewer/Password";
import { modelGlassesActions } from "../redux/brandViewer/modelGlasses";
import { brandViewerActions } from "../redux/brandViewer/brandViewer";
import { fetchModelMaterialPresets } from "../services/api/materialPresetsApi";
import logoSorry from "../assets/icon/ARShades_Logo_Sorry.svg"

/**
 * ARShades3dViewer - Componente per visualizzare singole varianti 3D
 * Gestisce la visualizzazione di una specifica variante di occhiali con tracciamento sessioni
 * Supporta sia route standard che route library
 */
function ARShades3dViewer() {
    // === ROUTER & REDUX ===
    const params = useParams();
    const location = useLocation();
    const dispatch = useDispatch();

    // Controllo del parametro legacy nell'URL per decidere quale visualizzatore utilizzare
    const queryParams = new URLSearchParams(location.search);
    const useLegacyViewer = queryParams.get("legacy") === "true";

    // === STATI PRINCIPALI ===
    // Dati del visualizzatore e modelli
    const [listVariant, setListVariant] = useState([]);
    const [modelHistory, setModelHistory] = useState([]);
    const [initialTime, setInitialTime] = useState();
    const [refCatalog, setRefCatalog] = useState();
    const [idViewer, setIdViewer] = useState();

    // Timing e animazioni
    const [initialModelTime, setInitiaModellTime] = useState(new Date());
    const [isWebView, setIsWebView] = useState(false);

    // Stati per errori e caricamento
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isCatalogExpired, setIsCatalogExpired] = useState(false);
    const [modelFromCache, setModelFromCache] = useState(null);

    // Sicurezza e accesso
    const [lock, setLock] = useState(false);

    // === ROUTE DETECTION ===
    const isLibraryRoute = params.TokenProduct && params.TokenVariant;

    const viewer = isLibraryRoute ? null : params.TokenViewer;
    const product = isLibraryRoute ? params.TokenProduct : params.TokenCatalogProduct;
    const variant = isLibraryRoute ? params.TokenVariant : params.TokenCatalogVariant;

    // === PROJECT PARAMS (per route library) ===
    const PROJECT_PARAMS = {
        ar: "true",
        autoRotate: false,
        backgroundColor: "#ffffff",
        isBackgroundImage: false,
        backgroundImage: "",
        cameraControls: true,
        cameraOrbit: "",
        disableZoom: false,
        exposure: 1,
        lightExposition: 1,
        shadowIntensity: 0.5,
        shadowSmooth: 0.3,
    };

    // === SELETTORI REDUX ===
    const project = useSelector((state) => state.brandViewer);
    const modelInView = useSelector((state) =>
        state.modelGlasses.find((model) => model.id === variant)
    );

    // === STRUTTURA SESSIONE ===
    const [session, setSession] = useState({
        deviceId: "",
        device: "",
        deviceOs: "",
        isFirstSession: false,
        catalogRef: "",
        sessionStartDate: "",
        sessionEndDate: "",
        list_catalog_product_refs: [],
        list_viewed_products: [],
        list_catalog_variant_refs: [],
        list_line_tag_refs: [],
        catalogProductRefsDoc: null,
        totalTime: 0,
        list_size_refs: [],
        referrer: "",
    });

    // === STATI AGGIUNTIVI ===
    const [initialModel, setInitialModel] = useState();
    const [src, setSrc] = useState("");
    const [pwd, setPwd] = useState("");
    const [listCreated, setListCreated] = useState(false);
    const [usdzSrc, setUsdzSrc] = useState("");
    const [materialPresetsMap, setMaterialPresetsMap] = useState(new Map());

    // Stati per la sessione in Firestore
    const [sessionRef, setSessionRef] = useState(null);
    const [isSessionExpired, setIsSessionExpired] = useState(false);

    // === EVENT HANDLERS ===
    /**
     * Sblocca il visualizzatore se protetto da password
     */
    function unlock() {
        setLock(false);
    }

    // === EFFECTS PER L'INIZIALIZZAZIONE ===

    /**
     * Effect: Inizializza il visualizzatore e il modello
     * Gestisce sia le route standard che quelle library
     */
    useEffect(() => {
        async function fetchInitialData() {
            if (isLibraryRoute) {
                // Route Library: non c'è viewer, setta PROJECT_PARAMS
                dispatch(brandViewerActions.setSelectedProject(PROJECT_PARAMS));
                setInitialModel(variant);
                setIdViewer(null); // non c'è viewer
                setInitialTime(new Date());
            } else {
                try {
                    const viewerRef = doc(db, "3DViewers", viewer);
                    let viewerSnap = await getDoc(viewerRef);

                    if (!viewerSnap.exists() || !viewerSnap.data()) {
                        // QUERY: cerca un documento dove il campo "catalog" è uguale a viewer, 
                        // perché prima era brand e alcuni link presentano questo campo errato
                        const viewersQuery = query(
                            collection(db, "3DViewers"),
                            where("catalog", "==", viewer)
                        );
                        const querySnapshot = await getDocs(viewersQuery);

                        if (!querySnapshot.empty) {
                            // Prendiamo il primo risultato trovato
                            viewerSnap = querySnapshot.docs[0];
                        } else {
                            setError("3D Viewer not found or missing data");
                            console.error("Visualizzatore 3D non trovato o dati mancanti");
                            return;
                        }
                    }

                    const data = viewerSnap.data();
                    setIdViewer(viewer);
                    dispatch(brandViewerActions.setSelectedProject(data));
                    setInitialModel(params.TokenCatalogVariant);

                    try {
                        const variantRef = await getDoc(doc(
                            collection(db, "3DViewers", viewer, "Products", product, "variants"),
                            variant
                        ));
                        if (variantRef && variantRef.exists() && variantRef.data()) {
                            // console.log('variantRef.data()', variantRef.data());
                            if (variantRef.data()?.shouldApply) {
                                dispatch(brandViewerActions.setSelectedProject(variantRef.data()));
                            }
                        }
                    } catch (variantError) {
                        console.error("Errore nel caricamento delle animazioni della variante:", variantError);
                    }

                    setInitialTime(new Date());
                } catch (error) {
                    console.error("Errore durante l'inizializzazione del visualizzatore:", error);
                    setError("An error occurred while loading the 3D viewer");
                }
            }
        }
        fetchInitialData();
    }, [viewer, dispatch, params.TokenCatalogVariant, isLibraryRoute]);

    /**
     * Effect: Carica i dati del modello e imposta altre variabili
     * Gestisce il caricamento diverso per route standard vs library
     */
    useEffect(() => {
        if ((!idViewer && !isLibraryRoute) || isCatalogExpired) return;
        if (isLibraryRoute) {
            fetchModelData("Products", "Variants", false);
        } else {
            fetchModelData("CataloguesProducts", "CataloguesVariants");
        }
    }, [variant, product, dispatch, idViewer, isLibraryRoute, isCatalogExpired]);

    /**
     * Effect: Verifica lo stato di lock del visualizzatore
     */
    useEffect(() => {
        if (!idViewer) return;

        async function fetchLock() {
            try {
                const docRef = await getDoc(doc(db, "3DViewers", idViewer));
                if (!docRef.exists() || !docRef.data()) {
                    console.error("Documento 3DViewer non trovato o dati mancanti");
                    return;
                }

                const data = docRef.data();
                setLock(data.lock || false);
                if (data.lock) {
                    setPwd(data.password || "");
                }
            } catch (error) {
                console.error("Errore durante il controllo del lock:", error);
                // Imposta un valore di default in caso di errore
                setLock(false);
            }
        }
        fetchLock();
    }, [idViewer]);

    // === UTILITY FUNCTIONS FOR SESSION ===
    /**
     * Ottiene il mese corrente (1-12)
     */
    function getCurrentMonth() {
        return new Date().getMonth() + 1;
    }

    /**
     * Ottiene l'anno corrente
     */
    function getCurrentYear() {
        return new Date().getFullYear();
    }

    // === EFFECTS PER LA GESTIONE DEI MODELLI E CONTATORI ===

    /**
     * Effect: Reset dello stato della cache quando cambia variante
     */
    useEffect(() => {
        // console.log("🔄 Reset modelFromCache per nuova variante:", variant);
        setModelFromCache(null);
    }, [variant]);

    /**
     * Effect: Aggiornamento dei modelli e dei contatori di banda
     * Gestisce il caricamento iniziale delle varianti e il tracciamento del consumo di banda
     */
    useEffect(() => {
        if (!idViewer) return;
        if (modelFromCache === null) return;
        async function updateModel() {
            if (idViewer && variant && !lock && !isCatalogExpired) {
                setTimeout(async () => {
                    try {
                        // Solo se il modello non è stato caricato dalla cache o se è la prima volta
                        const shouldUpdateBandwidth = modelFromCache === false || (!modelHistory.includes(idViewer?.src) && modelFromCache !== true);

                        if (shouldUpdateBandwidth) {
                            const variantS = await getDoc(doc(db, "CataloguesVariants", variant));
                            if (!variantS.exists() || !variantS.data() || !variantS.data().urlGlobalComplete) {
                                console.error("Dati mancanti per il modello");
                                return;
                            }

                            const srcData = variantS.data().urlGlobalComplete;
                            const fileImg = await fetch(srcData).then((r) => r.blob());
                            const sizeMb = fileImg.size / 1000000;
                            const b = sizeMb.toFixed(2);
                            const banda = parseFloat(b);

                            const docRef = await getDoc(doc(db, "3DViewers", idViewer));
                            const docCatalog = docRef.data().catalog;
                            const catalogRefSnap = await getDoc(doc(db, "Catalogues", docCatalog));
                            const dataDoc = catalogRefSnap.data()?.dataConsumption;
                            const meseCorrente = getCurrentMonth();
                            const year = getCurrentYear();

                            // Utilizzo transazione per gestire atomicamente i contatori
                            const contatoriRef = doc(db, "Contatori_Brand", dataDoc);
                            try {
                                await runTransaction(db, async (transaction) => {
                                    const contatoriDoc = await transaction.get(contatoriRef);
                                    if (!contatoriDoc.exists()) {
                                        console.error("Error: dataConsumption not found!");
                                        return; // Esce dalla transazione
                                    }

                                    const currentData = contatoriDoc.data();
                                    if (!currentData.bandwidth_3dviewer) {
                                        console.error("Error: bandwidth_3dviewer field not found!");
                                        return; // Esce dalla transazione
                                    }

                                    const updatedBandwidth = currentData.bandwidth_3dviewer.map((ele) => {
                                        if (meseCorrente === ele.month && year === ele.year) {
                                            return {
                                                bandwidth_month: ele.bandwidth_month + banda,
                                                calls_number: ele.calls_number + 1,
                                                month: ele.month,
                                                year: ele.year,
                                            };
                                        }
                                        return ele;
                                    });

                                    transaction.update(contatoriRef, {
                                        bandwidth_3dviewer: updatedBandwidth,
                                    });
                                });
                            } catch (error) {
                                console.error("Errore durante l'aggiornamento dei contatori:", error);
                                // Qui potresti implementare un fallback o retry logic
                            }
                        } else {
                            // console.log("🟢 Modello caricato da cache - bandwidth non aggiornata");
                        }

                        const variantRefId = doc(db, "CataloguesVariants", variant);
                        const variantRefSnap = await getDoc(variantRefId);

                        if (!variantRefSnap.exists() || !variantRefSnap.data()) {
                            console.error("Variante non trovata o dati mancanti");
                            return;
                        }

                        const variantData = variantRefSnap.data();

                        if (!listCreated) {
                            // Prima creazione della lista prodotti visualizzati
                            const date = new Date();
                            const timeDiff = Math.floor((date.getTime() - initialModelTime.getTime()) / 1000);
                            setInitiaModellTime(new Date());
                            const url = variantData.urlGlobalComplete;
                            if (!url) {
                                console.error("URL mancante per la variante");
                                return;
                            }

                            const blob = await fetch(url).then((r) => r.blob());
                            const b = blob.size / 1000000;
                            const b1 = b.toFixed(2);
                            const banda = parseFloat(b1) + 0.5;
                            const variantSize = variantData?.size?.size || "";

                            setSession(prevSession => ({
                                ...prevSession,
                                list_catalog_variant_refs: listVariant ? listVariant : "",
                                device: window.navigator.userAgent,
                                list_viewed_products: [{
                                    productName: variantData?.glassesName ? variantData?.glassesName : "",
                                    lineTagRef: prevSession?.list_line_tag_refs ? prevSession?.list_line_tag_refs[0] : "",
                                    catalogProductRef: prevSession?.catalogProductRefsDoc ? prevSession?.catalogProductRefsDoc : "",
                                    list_tried_variants: [
                                        {
                                            catalogVariantRefs: variantRefId.id,
                                            timeStart: initialModelTime,
                                            totalSeconds: timeDiff,
                                            sizeRef: variantSize,
                                        },
                                    ],
                                    timeStart: initialModelTime,
                                    consumedBandwidth: banda,
                                    date: initialModelTime,
                                    totalSeconds: timeDiff,
                                }],
                                list_size_refs: variantSize ? [variantSize] : []
                            }));
                            setListCreated(true);
                        } else {
                            // Aggiornamento per varianti successive
                            const date = new Date();
                            const timeDiff = Math.floor((date.getTime() - initialModelTime.getTime()) / 1000);
                            setInitiaModellTime(new Date());

                            const url = variantData.urlGlobalComplete;
                            if (!url) {
                                console.error("URL mancante per la variante");
                                return;
                            }

                            const blob = await fetch(url).then((r) => r.blob());
                            const b = blob.size / 1000000;
                            const b1 = b.toFixed(2);
                            const variantSize = variantData?.size?.size || "";

                            setSession(prevSession => {
                                // Verifica che ci siano prodotti visualizzati
                                if (!prevSession.list_viewed_products || !prevSession.list_viewed_products[0]) {
                                    console.error("Dati sessione mancanti");
                                    return prevSession;
                                }

                                const updatedViewedProducts = [...prevSession.list_viewed_products];
                                let updatedSizeRefs = [...prevSession.list_size_refs];

                                // Aggiungi la size se non esiste già
                                if (variantSize && !updatedSizeRefs.includes(variantSize)) {
                                    updatedSizeRefs.push(variantSize);
                                }

                                const currentProduct = updatedViewedProducts[0];
                                const listTriedVariants = [...currentProduct.list_tried_variants];

                                // Verifica se la variante esiste già
                                const existingVariantIndex = listTriedVariants.findIndex(
                                    variant => variant.catalogVariantRefs === variantRefId.id
                                );

                                if (existingVariantIndex !== -1) {
                                    // Aggiorna la variante esistente
                                    listTriedVariants[existingVariantIndex] = {
                                        ...listTriedVariants[existingVariantIndex],
                                        totalSeconds: listTriedVariants[existingVariantIndex].totalSeconds + timeDiff
                                    };
                                } else {
                                    // Aggiungi nuova variante
                                    listTriedVariants.push({
                                        catalogVariantRefs: variantRefId.id,
                                        timeStart: initialTime,
                                        totalSeconds: timeDiff,
                                        sizeRef: variantSize,
                                    });
                                }

                                const banda = currentProduct.consumedBandwidth + parseFloat(b1);

                                updatedViewedProducts[0] = {
                                    ...currentProduct,
                                    productName: variantData.glassesName,
                                    lineTagRef: prevSession.list_line_tag_refs[0],
                                    catalogProductRef: prevSession.catalogProductRefsDoc,
                                    list_tried_variants: listTriedVariants,
                                    timeStart: date,
                                    consumedBandwidth: banda,
                                    totalSeconds: currentProduct.totalSeconds + timeDiff,
                                };

                                return {
                                    ...prevSession,
                                    totalTime: prevSession.totalTime + timeDiff,
                                    list_viewed_products: updatedViewedProducts,
                                    list_size_refs: updatedSizeRefs
                                };
                            });
                        }
                        // Reset dello stato per evitare race conditions con il prossimo modello
                        setModelFromCache(null);
                    } catch (error) {
                        console.error("Errore durante l'aggiornamento del modello:", error);
                    }
                }, 1000);
            }
        }
        updateModel();
    }, [variant, lock, idViewer, isCatalogExpired, modelFromCache]);

    // === EFFECTS PER LA GESTIONE DELLA SESSIONE FIRESTORE ===

    /**
     * Effect: Gestione dell'evento beforeunload per salvare la sessione all'uscita
     */
    useEffect(() => {
        function handleUnload() {
            if (!sessionRef || isCatalogExpired) return;

            // Ottieni la data e l'ora attuale
            const date = new Date();
            const timeDiff = Math.floor((date.getTime() - initialModelTime.getTime()) / 1000);

            // Crea una nuova versione aggiornata della sessione
            const updatedSession = {
                ...session,
                totalTime: timeDiff,
                sessionEndDate: formatSessionDate(date),
                list_viewed_products: session.list_viewed_products.map((item) => ({
                    ...item,
                    totalSeconds: timeDiff,
                    list_tried_variants: item.list_tried_variants.map((variant) => ({
                        ...variant,
                        totalSeconds: timeDiff,
                    })),
                })),
            };

            // Aggiorna Firestore con la sessione aggiornata
            updateDoc(sessionRef, updatedSession)
                .catch((error) => {
                    console.error("Errore nell'aggiornamento della sessione:", error);
                });
        }

        window.addEventListener("beforeunload", handleUnload);
        if (isWebView) return;
        return () => {
            window.removeEventListener("beforeunload", handleUnload);
        };
    }, [sessionRef, initialModelTime, isWebView, isCatalogExpired]);

    /**
     * Effect: Timeout di 4 minuti per la sessione
     */
    useEffect(() => {
        if (isLibraryRoute) return;
        const sessionTimeout = setTimeout(() => {
            setIsSessionExpired(true);
        }, 240000);
        return () => clearTimeout(sessionTimeout);
    }, []);

    /**
     * Effect: Crea la sessione in Firestore quando i dati minimi sono pronti
     */
    useEffect(() => {
        if (isLibraryRoute) return;
        // Condizione: crea la sessione solo se ci sono dati minimi e il catalogo non è scaduto
        if (!session.deviceId || !session.catalogProductRefsDoc || isCatalogExpired) return;

        let alreadyCreated = false;
        async function addSession() {
            if (alreadyCreated) return;
            alreadyCreated = true;
            try {
                const sessioneRef = doc(collection(db, "Session_3DViewer"));
                await setDoc(sessioneRef, session);
                setSessionRef(sessioneRef);
            } catch (error) {
                console.error("Errore durante la creazione della sessione:", error);
                setError("An error occurred while creating the session");
            }
        }
        addSession();
    }, [session.deviceId, session.catalogProductRefsDoc, isCatalogExpired]);

    /**
     * Effect: Aggiornamenti periodici della sessione ogni 10 secondi
     */
    useEffect(() => {
        if (sessionRef && !isSessionExpired && !isCatalogExpired) {
            const interval = setInterval(() => {
                const date = new Date();
                const timeDiff = Math.floor((date.getTime() - initialModelTime.getTime()) / 1000);

                setSession(prevSession => {
                    const updatedSession = {
                        ...prevSession,
                        totalTime: timeDiff,
                        sessionEndDate: formatSessionDate(date),
                        list_viewed_products: prevSession.list_viewed_products.map((item) => ({
                            ...item,
                            totalSeconds: timeDiff,
                            list_tried_variants: item.list_tried_variants.map((variant) => ({
                                ...variant,
                                totalSeconds: timeDiff,
                            })),
                        })),
                    };

                    updateDoc(sessionRef, updatedSession).catch((error) => {
                        console.error("Error updating document: ", error);
                    });

                    return updatedSession;
                });

            }, 5000);
            return () => clearInterval(interval);
        }
    }, [sessionRef, initialModelTime, isSessionExpired, isCatalogExpired]);

    /**
     * Effect: Rileva quando il caricamento è completo
     */
    useEffect(() => {
        if (src || error) {
            // Diamo un piccolo ritardo per garantire che i dati si carichino completamente
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [src, error]);

    // === MAIN FUNCTION ===

    /**
     * Carica i dati del modello dalla collezione specificata
     * @param {string} productCollection - Nome della collezione prodotti
     * @param {string} variantCollection - Nome della collezione varianti  
     * @param {boolean} useViewerData - Se utilizzare i dati del viewer per la sessione
     */
    async function fetchModelData(productCollection, variantCollection, useViewerData = true) {
        try {
            // Verifica che variant sia definito
            if (!variant) {
                console.error("TokenCatalogVariant non definito o mancante");
                setError("The model variant is not specified");
                return;
            }

            const variantRef = await getDoc(doc(db, variantCollection, variant));
            if (!variantRef.exists()) {
                console.error(`Variante con ID ${variant} non trovata nel database`);
                setError(`Variant not found`);
                return;
            }

            const variantData = variantRef.data();
            if (!variantData || !variantData.urlGlobalComplete) {
                console.error(`Dati della variante incompleti o URL GLB mancante per la variante ${variant}`);
                setError("Incomplete variant data or missing GLB URL");
                return;
            }

            // Verifica se il catalogo è scaduto PRIMA di caricare il modello
            if (useViewerData) {
                const docRef = await getDoc(doc(db, "3DViewers", idViewer));
                const docCatalog = docRef.data().catalog;
                const catalogRefDoc = doc(db, "Catalogues", docCatalog);
                const catalogRefSnap = await getDoc(catalogRefDoc);

                if (!catalogRefSnap.exists()) {
                    console.error("Error: catalog not found!");
                    setError('Catalog not found.');
                    return;
                }

                if (catalogRefSnap.data().isExpired === true) {
                    console.error("Error: catalog is expired!");
                    setError('ARSHADES license has expired or is inactive.');
                    setIsCatalogExpired(true);
                    return;
                }
            }

            // Puliamo il modello attuale nello store
            dispatch(modelGlassesActions.clearModels());

            // Aggiungiamo il nuovo modello
            dispatch(modelGlassesActions.setModel({ data: removeUnwantedFields(variantData, ['lastUpdate']), id: variant }));

            setSrc(variantData.urlGlobalComplete);
            setUsdzSrc(variantData.urlUsdzComplete);

            if (variantData.list_material_presets) {
                const presetsMap = await fetchModelMaterialPresets(variantData);
                setMaterialPresetsMap(presetsMap);
            }

            if (useViewerData) {
                // Ottiene deviceID (fallback a uuid in caso di errore)
                let deviceID = uuidv4();
                try {
                    const res = await axios.get("https://api.ipify.org/?format=json");
                    deviceID = res.data.ip;
                } catch (error) {
                    console.error("Failed to fetch IP address:", error);
                }

                let sameMonthYear = false;
                const padWithZero = (number) => number.toString().padStart(2, "0");
                const date = new Date();
                const year = date.getFullYear();
                const month = padWithZero(date.getMonth() + 1);
                const monthDataCons = date.getMonth() + 1;
                const day = padWithZero(date.getDate());
                const hours = padWithZero(date.getHours());
                const minutes = padWithZero(date.getMinutes());
                const seconds = padWithZero(date.getSeconds());

                // Catalogo già verificato prima del caricamento del modello
                const docRef = await getDoc(doc(db, "3DViewers", idViewer));
                const docCatalog = docRef.data().catalog;
                const catalogRefDoc = doc(db, "Catalogues", docCatalog);
                const catalogRefSnap = await getDoc(catalogRefDoc);

                const dataConsumptionDoc = catalogRefSnap.data().dataConsumption;
                if (dataConsumptionDoc === null) {
                    console.error("Error: data consumption value is null!");
                }
                const contatoriRef = await getDoc(doc(db, "Contatori_Brand", dataConsumptionDoc));
                if (!contatoriRef.exists()) {
                    console.error("Error: dataConsumption not found!");
                }
                const dataConsumption = contatoriRef.data();
                const productRefDoc = doc(db, productCollection, product);
                const productDoc = await getDoc(productRefDoc);
                if (!productDoc.exists()) {
                    console.error("Error: product not found!");
                }

                // Verifica che productDoc e productDoc.data() esistano prima di accedere a list_tags
                let sizeRefDoc = null;
                if (variantData.size) {
                    sizeRefDoc = variantData.size.size;
                }

                setRefCatalog(catalogRefDoc);

                const userAgent = navigator.userAgent;
                let browserName;
                const isWV = /\bwv\b/.test(userAgent);
                setIsWebView(true);

                if (userAgent.indexOf("Firefox") > -1) {
                    browserName = "Mozilla Firefox";
                } else if (userAgent.indexOf("Chrome") > -1) {
                    browserName = "Google Chrome";
                } else if (userAgent.indexOf("Safari") > -1) {
                    browserName = "Apple Safari";
                } else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) {
                    browserName = "Opera";
                } else if (userAgent.indexOf("Trident") > -1) {
                    browserName = "Microsoft Internet Explorer";
                } else if (userAgent.indexOf("Edge") > -1) {
                    browserName = "Microsoft Edge";
                } else {
                    browserName = "Unknown";
                }
                // Verifica se l'utente ha già visitato il sito
                const isFirstSession = !Cookies.get("visited");
                if (!Cookies.get("visited")) {
                    Cookies.set("visited", "true");
                }

                setSession(prevSession => ({
                    ...prevSession,
                    deviceId: deviceID,
                    deviceOs: browserName,
                    referrer: document.referrer.trim() !== '' ? document.referrer : 'UNKNOWN',
                    list_size_refs: sizeRefDoc ? [sizeRefDoc] : [],
                    isFirstSession: isFirstSession,
                    catalogProductRefsDoc: productDoc.exists() ? productDoc.id : "",
                    list_line_tag_refs: (productDoc.exists() && productDoc.data() && productDoc.data().list_line_tags && productDoc.data().list_line_tags.length > 0)
                        ? [productDoc.data().list_line_tags[0]]
                        : [],
                    list_catalog_product_refs: productDoc.exists() ? [productDoc.id] : [],
                    catalogRef: docCatalog,
                    sessionStartDate: `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`,
                }));

                if (dataConsumption && dataConsumption.bandwidth_3dviewer) {
                    dataConsumption.bandwidth_3dviewer.map((bandw) => {
                        if (bandw.month === monthDataCons && bandw.year === year) {
                            sameMonthYear = true;
                        }
                    });
                    if (!sameMonthYear) {
                        // Utilizzo transazione atomica per aggiungere nuovo record mese/anno
                        const contatoriRef = doc(db, "Contatori_Brand", dataConsumptionDoc);

                        try {
                            await runTransaction(db, async (transaction) => {
                                const contatoriDoc = await transaction.get(contatoriRef);
                                const currentData = contatoriDoc.data();
                                // Ricontrolla se il record esiste (potrebbero averlo aggiunto altri utenti nel frattempo)
                                const existingRecord = currentData.bandwidth_3dviewer.find(
                                    bandw => bandw.month === monthDataCons && bandw.year === year
                                );

                                if (!existingRecord) {
                                    transaction.update(contatoriRef, {
                                        bandwidth_3dviewer: [
                                            ...currentData.bandwidth_3dviewer,
                                            { bandwidth_month: 0, calls_number: 0, month: monthDataCons, year }
                                        ]
                                    });
                                }
                            });
                        } catch (error) {
                            console.error("Errore durante l'aggiunta del nuovo record:", error);
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Errore durante il caricamento dei dati del modello:", error);
            // Qui puoi anche settare uno stato per mostrare un messaggio di errore all'utente
            setError(error.message);
        }
    }

    // === UTILITY FUNCTIONS ===
    /**
     * Formatta una data nel formato utilizzato per le sessioni
     */
    function formatSessionDate(date) {
        const padWithZero = (num) => num.toString().padStart(2, '0');
        return `${date.getFullYear()}/${padWithZero(date.getMonth() + 1)}/${padWithZero(date.getDate())} ${padWithZero(date.getHours())}:${padWithZero(date.getMinutes())}:${padWithZero(date.getSeconds())}`;
    }

    /**
     * Rimuove ricorsivamente i campi indesiderati dagli oggetti (es: lastUpdate)
     */
    function removeUnwantedFields(obj, fieldsToRemove = ['lastUpdate']) {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(item => removeUnwantedFields(item, fieldsToRemove));
        const newObj = {};
        for (const key in obj) {
            if (fieldsToRemove.includes(key)) continue;
            if (typeof obj[key] === 'object') {
                newObj[key] = removeUnwantedFields(obj[key], fieldsToRemove);
            } else {
                newObj[key] = obj[key];
            }
        }
        return newObj;
    }

    // === CALLBACKS ===
    // Callback per gestire lo stato della cache del modello
    const handleModelCacheStatus = (fromCache) => {
        setModelFromCache(fromCache);
        if (fromCache !== null) {
            console.log(fromCache ? "🟢 Modello caricato da cache" : "🔵 Modello scaricato da Firebase Storage");
        }
    };

    // === RENDER ===
    return (
        <div>
            {/* Controllo errori generali */}
            {error && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    zIndex: 10
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        padding: '30px',
                        maxWidth: '80%',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        textAlign: 'center'
                    }}>
                        <img src={logoSorry} style={{ width: "30%", padding: "1px", marginBottom: "3px" }} alt="Not supported" />
                        <div style={{
                            marginBottom: '15px',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#343a40'
                        }}>
                            {isCatalogExpired ? 'Sorry' : 'Error'}
                        </div>
                        <div style={{
                            color: '#6c757d',
                            maxWidth: '400px',
                            lineHeight: '1.5'
                        }}>
                            {error}
                            <br /><br />
                            {isCatalogExpired && 'If you are the owner, please contact us.'}
                        </div>
                    </div>
                </div>
            )}

            {/* Controllo accesso con password */}
            {lock ? (
                <Password unlock={unlock} password={pwd} />
            ) : (
                <ModelContainerFrontEnd modelSelected={modelInView} project={project[0]} singleView>
                    {/* Visualizzatori 3D - solo se non è Facebook Browser */}
                    {!error && !navigator.userAgent.match(/FBAV/i) && (
                        <>
                            {useLegacyViewer ? (
                                <ProjectModelViewer
                                    singleView
                                    singleSrc={src}
                                    project={project[0]}
                                    viewerPage
                                    paramsId={params.TokenCatalogProduct}
                                    urlView={isLibraryRoute
                                        ? `https://studio.arshades.it/Arshades3dLibrary/${params.TokenProduct}/variant/${params.TokenVariant}`
                                        : `https://studio.arshades.it/Arshades3d/${params.TokenViewer}/glasses/${params.TokenCatalogProduct}/variant/${params.TokenCatalogVariant}`}
                                />
                            ) : (
                                <ThreeJSViewer
                                    singleView
                                    singleSrc={src}
                                    singleUsdzSrc={usdzSrc}
                                    viewerPage
                                    paramsId={params.TokenCatalogProduct}
                                    urlView={isLibraryRoute
                                        ? `https://studio.arshades.it/Arshades3dLibrary/${params.TokenProduct}/variant/${params.TokenVariant}`
                                        : `https://studio.arshades.it/Arshades3d/${params.TokenViewer}/glasses/${params.TokenCatalogProduct}/variant/${params.TokenCatalogVariant}`
                                    }
                                    modelMaterialPresets={materialPresetsMap ? materialPresetsMap : null}
                                    onModelCacheStatus={handleModelCacheStatus}
                                />
                            )}
                        </>
                    )}

                    {/* Messaggio "Model not available" - mostrato solo dopo il caricamento */}
                    {!isLoading && !src && variant && !error && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            zIndex: 10
                        }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                padding: '30px',
                                maxWidth: '80%',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    marginBottom: '15px',
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    color: '#343a40'
                                }}>
                                    Model not available
                                </div>
                                <div style={{
                                    color: '#6c757d',
                                    maxWidth: '400px',
                                    lineHeight: '1.5'
                                }}>
                                    The 3D model you are looking for is currently not available. This may be due to the model being removed or an incorrect link.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Fallback per Facebook Browser */}
                    {navigator.userAgent.match(/FBAV/i) &&
                        !navigator.userAgent.match(/(iPod|iPhone|iPad)/) && (
                            <div
                                id={
                                    project?.menuPosition === "left" || project?.menuPosition === "right"
                                        ? "projectTwo"
                                        : "project"
                                }
                            >
                                <img
                                    src={modelInView?.not_supported_image}
                                    style={{ width: "100%" }}
                                    alt="Not supported"
                                />
                                il broswer di facebook non supporta modelli 3D e realtà aumentata. prova con un altro broswer
                            </div>
                        )}
                </ModelContainerFrontEnd>
            )}
        </div>
    );
}

export default ARShades3dViewer;